import { apiClient } from "./api-client";
import { toArray } from "./api-utils";
import type { AddProjectRequest } from "./api-types";
import type { ProjectsUsersAndGroups, TriyoProject } from "@/types/models";

/** Body for /projects/GetProjectsWithFilters (SortAndPageDto<FilterDto<int>>). */
export interface ProjectPageQuery {
  pageNumber: number;
  pageSize: number;
  sortBy?: string;
  sortDesc?: boolean;
}

export const ProjectsApi = {
  /** Every project the user can see, unpaged. */
  getUserProjects: async (): Promise<TriyoProject[]> => {
    const res = await apiClient.get<unknown>("/projects/getuserprojects");
    return toArray<TriyoProject>(res.data, "Projects", "projects");
  },

  /**
   * One page of projects, newest-modified first (the same call Triyo.Web's
   * project list makes). The API applies paging and sorting only — its `filters`
   * list is accepted but ignored — so status filtering stays client-side, and
   * the response is a bare array with no total count.
   */
  getProjectPage: async (query: ProjectPageQuery): Promise<TriyoProject[]> => {
    const res = await apiClient.post<unknown>("/projects/GetProjectsWithFilters", {
      pageNumber: query.pageNumber,
      pageSize: query.pageSize,
      sortBy: query.sortBy ?? "",
      sortDesc: query.sortDesc ?? true,
      skip: 0,
      take: 0,
      type: "",
      filters: [],
    });
    return toArray<TriyoProject>(res.data, "Projects", "projects");
  },

  /** Members/owners roster for the Create Deal wizard (requires Projects/View). */
  getUsersAndGroups: async (): Promise<ProjectsUsersAndGroups> => {
    const res = await apiClient.get<ProjectsUsersAndGroups>("/projects/GetProjectsUsersAndGroups");
    return res.data ?? {};
  },

  /** Creates a project ("Deal"); resolves the created project with its new Id. */
  addProject: async (payload: AddProjectRequest): Promise<TriyoProject> => {
    const res = await apiClient.post<TriyoProject>("/projects/AddProject", payload);
    return res.data ?? {};
  },
};
